import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
    changeTotalScore,
    setCurrentAnswerStatus,
} from '../../store/sessionStatus';
import AnswerImage from './UIComponents/AnswerImage';
import TargetFieldImage from './UIComponents/TargetFieldImage';
import Button from '../../components/button/Button';

import {
    DragOverHandler,
    DragStartHandler,
    DropHandler,
} from './dragFunctions';
import { TouchEnd, TouchStart } from './touchFunctions';

import './type5.css';

export default function Type5({ question, answer }) {
    const dispatch = useDispatch();
    const ref = useRef();
    const targetRef = useRef();

    const [answers, setAnswers] = useState(question.answers);
    const [disabledButton, setDisabledButton] = useState(false);
    const [draggable, setDraggable] = useState(true);

    const [imageState, setImageState] = useState({
        imageLeft: 0,
        imageTop: 0,
        imageHeight: 0,
        imageWidth: 0,
    });
    const [cardState, setCardState] = useState({
        index: '',
        title: '',
        targetCard: '',
    });

    const handleOnLoad = () => {
        setImageState((prevState) => {
            return {
                ...prevState,
                imageLeft: ref.current.offsetLeft,
                imageTop: ref.current.offsetTop,
                imageHeight: ref.current.getBoundingClientRect().height,
                imageWidth: ref.current.getBoundingClientRect().width,
            };
        });
    };

    const findLeftPosition = useCallback(
        (x) => {
            const leftPosition = `${
                imageState.imageLeft + imageState.imageWidth * x
            }px`;
            return leftPosition;
        },
        [imageState.imageLeft, imageState.imageWidth],
    );

    const findTopPosition = useCallback(
        (y) => {
            const topPosition = `calc(${
                imageState.imageTop + imageState.imageHeight * y
            }px + 20% )`;
            return topPosition;
        },
        [imageState.imageTop, imageState.imageHeight],
    );

    const handleWindowResize = () => {
        if (ref.current?.complete) {
            handleOnLoad();
        }
    };

    useEffect(() => {
        window.addEventListener('resize', handleWindowResize);
        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
    });

    useEffect(() => {
        setAnswers((current) =>
            current.map((card) => {
                if (card.target) {
                    const targetIndex = card.target - 1;
                    const leftPosition = findLeftPosition(
                        current[targetIndex].to.x,
                    );
                    const topPosition = findTopPosition(
                        current[targetIndex].to.y,
                    );
                    return {
                        ...card,
                        current: {
                            left: leftPosition,
                            top: topPosition,
                        },
                        target: card.target,
                    };
                }
                return card;
            }),
        );
    }, [imageState, findLeftPosition, findTopPosition]);

    function checkAnswer() {
        setAnswers((prevState) =>
            prevState.map((answer, i) => {
                const bg = answer.title
                    ? answer.target - 1 === i
                        ? 'linear-gradient(135deg, #48CFAD 0.55%, #19A784 97.3%)'
                        : 'linear-gradient(135deg, #EC87C0 0.55%, #BF4C90 97.3%)'
                    : 'transparent';
                return { ...answer, background: bg, isDropped: false };
            }),
        );

        setDraggable(false);

        const status = !answers.some((answer, i) => answer.target - 1 !== i);
        dispatch(setCurrentAnswerStatus(status));

        if (status) {
            dispatch(changeTotalScore(question.points));
        }
        setTimeout(() => answer(), 1500);
        setDisabledButton(true);
    }

    return (
        <div className="questionWrapper">
            <div className="type5-textArea">{question.text}</div>
            <div className="type5-answerOptions">
                {answers.map((card, i) => {
                    if (card.detail)
                        return (
                            <AnswerImage
                                key={i}
                                draggable={draggable}
                                onDragOver={(e) => DragOverHandler(e, i)}
                                onDrop={(e) =>
                                    DropHandler(
                                        e,
                                        0,
                                        0,
                                        i,
                                        cardState,
                                        setAnswers,
                                    )
                                }
                                left={card.current.left}
                                top={card.current.top}
                                background={card.isDropped}
                                onDragStart={(e) =>
                                    DragStartHandler(
                                        e,
                                        card,
                                        i,
                                        cardState,
                                        setCardState,
                                    )
                                }
                                onTouchStart={(e) => {
                                    if (!disabledButton) {
                                        TouchStart(
                                            e,
                                            i,
                                            cardState,
                                            setCardState,
                                            setAnswers,
                                        );
                                    }
                                }}
                                onTouchMove={(e) => {
                                    if (!disabledButton)
                                        TouchStart(
                                            e,
                                            i,
                                            cardState,
                                            setCardState,
                                            setAnswers,
                                        );
                                }}
                                onTouchEnd={(e) => {
                                    if (!disabledButton)
                                        TouchEnd(e, i, targetRef, setAnswers);
                                }}
                                width={`${
                                    imageState.imageWidth * card.width
                                }px`}
                                height={`${
                                    imageState.imageHeight * card.height
                                }px`}
                                src={card.detail}
                                zIndex={card.current.left ? 1 : 2}
                            />
                        );
                })}
            </div>
            <div className="type5-imageArea">
                <img
                    ref={ref}
                    className="image"
                    src={question.image}
                    onLoad={handleOnLoad}
                    alt={question.text}
                />
                <div ref={targetRef} className="type5-targetOptions">
                    {answers.map((answer, i) => {
                        const leftPosition = findLeftPosition(answer.to.x);
                        const topPosition = findTopPosition(answer.to.y);

                        return (
                            <TargetFieldImage
                                key={i}
                                onDragOver={(e) => DragOverHandler(e)}
                                onDrop={(e) =>
                                    DropHandler(
                                        leftPosition,
                                        topPosition,
                                        i,
                                        cardState,
                                        setAnswers,
                                    )
                                }
                                left={leftPosition}
                                top={topPosition}
                                background={answer.background}
                                width={`${
                                    imageState.imageWidth * answer.width
                                }px`}
                                height={`${
                                    imageState.imageHeight * answer.height
                                }px`}
                            />
                        );
                    })}
                </div>
            </div>
            <div className="nextQuestionButton">
                <Button
                    disabled={disabledButton}
                    type={'checkButton'}
                    text={'check'}
                    action={checkAnswer}
                />
            </div>
        </div>
    );
}
